import type { CSSProperties, ReactNode } from "react";
import { useRef, useState } from "react";
import { cn } from "../lib/utils";

interface SwipeCardProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
  className?: string;
  style?: CSSProperties;
}

export function SwipeCard({
  children,
  onSwipeLeft,
  onSwipeRight,
  threshold = 80,
  className,
  style,
}: SwipeCardProps) {
  const startX = useRef(0);
  const [drag, setDrag] = useState(0);
  const [dismissed, setDismissed] = useState<null | "left" | "right">(null);
  const dragging = useRef(false);

  function onPointerDown(e: React.PointerEvent) {
    startX.current = e.clientX;
    dragging.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging.current) return;
    setDrag(e.clientX - startX.current);
  }

  function onPointerUp() {
    if (!dragging.current) return;
    dragging.current = false;
    if (drag > threshold) {
      setDismissed("right");
      setTimeout(() => onSwipeRight?.(), 300);
    } else if (drag < -threshold) {
      setDismissed("left");
      setTimeout(() => onSwipeLeft?.(), 300);
    } else {
      setDrag(0);
    }
  }

  const rotate = drag * 0.08;
  const opacity = dismissed ? 0 : Math.max(0, 1 - Math.abs(drag) / (threshold * 3));

  const transforms = dismissed === "left"
    ? "translateX(-120%) rotate(-20deg)"
    : dismissed === "right"
    ? "translateX(120%) rotate(20deg)"
    : `translateX(${drag}px) rotate(${rotate}deg)`;

  return (
    <div
      className={cn("swipe-card", dismissed && "swipe-card--dismissed", className)}
      style={{
        transform: transforms,
        opacity,
        cursor: dragging.current ? "grabbing" : "grab",
        transition: dragging.current ? "none" : "transform 0.3s ease, opacity 0.3s ease",
        ...style,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {drag > threshold / 2 && (
        <div className="swipe-hint swipe-hint--right">✓</div>
      )}
      {drag < -threshold / 2 && (
        <div className="swipe-hint swipe-hint--left">✕</div>
      )}
      {children}
    </div>
  );
}
